import { useEffect, useState } from 'react'

import { api, ErrorApi, type CotizacionGuardada, type ResultadoCotizacion, type ResultadoOffgrid } from '../api'
import { anios, fechaCorta, num, pct, usd } from '../formato'
import { CLASE_ESTADO, ETIQUETA_ESTADO } from './EstadoVersion'

/**
 * Dos versiones del mismo cliente en columnas. Todo sale de lo GUARDADO (entrada y
 * resultado de cada version): comparar no recalcula nada, asi se ve lo que se le
 * presento al cliente en cada momento. Las filas que difieren se resaltan.
 */

interface Fila {
  etiqueta: string
  valor: (c: CotizacionGuardada) => string
}

const tipo = (c: CotizacionGuardada) =>
  c.tipo === 'offgrid'
    ? c.potencia_kwp > 0
      ? 'Off-grid'
      : 'Respaldo sin paneles'
    : c.tipo === 'industrial'
      ? 'Industrial trifásico'
      : c.con_bateria
        ? 'Híbrido'
        : 'On-grid'

/** El resultado con consumo y financiero, o null si es un off-grid (no los tiene). */
const estandar = (c: CotizacionGuardada): ResultadoCotizacion | null =>
  c.tipo === 'offgrid' ? null : (c.resultado as ResultadoCotizacion)

const FILAS: Fila[] = [
  { etiqueta: 'Fecha', valor: (c) => `${fechaCorta(c.created_at)} · ${c.creado_por_nombre}` },
  { etiqueta: 'Sistema', valor: tipo },
  { etiqueta: 'Estado', valor: (c) => ETIQUETA_ESTADO[c.estado] },
  {
    etiqueta: 'Consumo',
    valor: (c) => {
      const r = estandar(c)
      if (!r) return '—'
      const u = r.consumo_usado
      const kwh = u?.kwh_mes ?? r.dimensionado.consumo_kwh_mes
      return `${num(kwh)} kWh/mes${u?.fuente === 'proyeccion' ? ' (proyectado)' : ''}`
    },
  },
  { etiqueta: 'Demanda', valor: (c) => { const d = estandar(c)?.consumo_usado?.demanda_kw; return d != null ? `${num(d)} kW` : '—' } },
  { etiqueta: 'Potencia', valor: (c) => `${num(c.potencia_kwp)} kWp` },
  { etiqueta: 'Paneles', valor: (c) => `${estandar(c)?.dimensionado.paneles ?? (c.resultado as ResultadoOffgrid).sistema.paneles}` },
  { etiqueta: 'Descuento', valor: (c) => (c.resultado.totales.valor_descuento > 0 ? `${pct(c.resultado.totales.descuento_pct)} (${usd(c.resultado.totales.valor_descuento)})` : 'Sin descuento') },
  { etiqueta: 'Total con IVA', valor: (c) => usd(c.total) },
  { etiqueta: 'VAN', valor: (c) => (c.van === null ? '—' : usd(c.van)) },
  { etiqueta: 'TIR', valor: (c) => pct(c.tir) },
  { etiqueta: 'Recuperación', valor: (c) => { const r = estandar(c); return r ? anios(r.financiero.payback_anios) : '—' } },
  { etiqueta: 'Ahorro año 1', valor: (c) => { const r = estandar(c); return r ? usd(r.financiero.ahorro_primer_anio) : '—' } },
  {
    etiqueta: 'Esperar 12 meses',
    valor: (c) => {
      const e = estandar(c)?.costo_esperar
      if (!e) return '—'
      if ('error' in e) return 'No aplica'
      const doce = e.filas.find((f) => f.meses === 12)
      return doce ? usd(doce.costo_total) : '—'
    },
  },
  { etiqueta: 'Nota', valor: (c) => c.nota || '—' },
]

export function CompararVersiones({ ids, alVolver }: { ids: [string, string]; alVolver: () => void }) {
  const [versiones, setVersiones] = useState<CotizacionGuardada[] | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    Promise.all(ids.map((id) => api.cotizacion(id)))
      .then((r) => setVersiones(r.map((x) => x.cotizacion).sort((a, b) => a.version - b.version)))
      .catch((err) => setError(err instanceof ErrorApi ? err.message : 'No se pudieron cargar las versiones.'))
  }, [ids])

  return (
    <section>
      <button className="enlace volver" onClick={alVolver}>
        ← Volver al cliente
      </button>
      <h1>Comparar versiones</h1>
      {error && <p className="error">{error}</p>}
      {versiones && (
        <div className="tabla-envoltura">
          <table className="tabla comparar">
            <thead>
              <tr>
                <th />
                {versiones.map((v) => (
                  <th key={v.id}>
                    v{v.version} <span className={`insignia ${CLASE_ESTADO[v.estado]}`}>{ETIQUETA_ESTADO[v.estado]}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FILAS.map((f) => {
                const valores = versiones.map(f.valor)
                return (
                  <tr key={f.etiqueta} className={valores[0] !== valores[1] ? 'sin-clic difiere' : 'sin-clic'}>
                    <td className="suave">{f.etiqueta}</td>
                    {valores.map((v, i) => (
                      <td key={i}>{v}</td>
                    ))}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      {versiones && <p className="suave pequeno-texto">Resaltado: lo que cambia entre las dos. Son las cifras guardadas con cada versión, sin recalcular.</p>}
    </section>
  )
}
